// Pure, framework-free converters between the Boxes' canonical string values and richer
// runtime representations. These are the *only* place value representation is translated —
// the `*Box` components always speak canonical strings; the form-bound `*Field` wrappers and
// any non-form call site use these to bridge to `Date` / `[Date, Date]` / minutes / seconds.
//
// Canonical strings (see date-time-field/README): date `"YYYY-MM-DD"`, datetime
// `"YYYY-MM-DDTHH:mm"`, range `"A..B"` (open ends `"A..INF"` / `"-INF..B"`, empty `""`),
// time `"HH:mm"`.

import {
  formatIsoDate,
  formatIsoDateTime,
  pad2,
  parseIsoDate,
  parseIsoDateTime,
} from './internal/dateCore';

/* ------------------------------------------------------------------ *
 * Date — single
 * ------------------------------------------------------------------ */

/** `Date | null` → `"YYYY-MM-DD"` (empty string when null). */
export function dateToIso(value: Date | null): string {
  return value ? formatIsoDate(value) : '';
}

/** `"YYYY-MM-DD"` (time part, if any, is ignored) → `Date | null`. */
export function isoToDate(value: string): Date | null {
  const datePart = (value ?? '').trim().split('T')[0] ?? '';
  return datePart ? parseIsoDate(datePart) : null;
}

/* ------------------------------------------------------------------ *
 * DateTime — single
 * ------------------------------------------------------------------ */

/** `Date | null` → `"YYYY-MM-DDTHH:mm"`. */
export function dateToIsoDateTime(value: Date | null): string {
  return value ? formatIsoDateTime(value) : '';
}

/** `"YYYY-MM-DDTHH:mm"` → `Date | null`. */
export function isoDateTimeToDate(value: string): Date | null {
  return value ? parseIsoDateTime(value) : null;
}

/* ------------------------------------------------------------------ *
 * Range — date pair
 * ------------------------------------------------------------------ */

export type DatePair = [Date | null, Date | null];

const OPEN_OR_EMPTY = new Set(['', 'INF', '-INF']);

/**
 * `"A..B"` → `[Date | null, Date | null]`. Open (`INF`/`-INF`) and not-yet-picked (empty)
 * endpoints both collapse to `null` — the ∞ vs. "unset" distinction survives only in string form.
 */
export function rangeToDatePair(value: string, opts?: { withTime?: boolean }): DatePair {
  const raw = (value ?? '').trim();
  if (raw === '') return [null, null];
  const [a = '', b = ''] = raw.split('..');
  const parse = opts?.withTime
    ? parseIsoDateTime
    : (s: string) => parseIsoDate(s.split('T')[0] ?? '');
  return [OPEN_OR_EMPTY.has(a) ? null : parse(a), OPEN_OR_EMPTY.has(b) ? null : parse(b)];
}

/**
 * `[Date | null, Date | null]` → `"A..B"`. A null endpoint becomes empty (`"A.."` / `"..B"`);
 * both null → `""`. Never emits `INF`/`-INF` (a Date pair cannot express ∞).
 */
export function datePairToRange(pair: DatePair, opts?: { withTime?: boolean }): string {
  const format = opts?.withTime ? formatIsoDateTime : formatIsoDate;
  const [from, to] = pair;
  if (!from && !to) return '';
  return `${from ? format(from) : ''}..${to ? format(to) : ''}`;
}

/* ------------------------------------------------------------------ *
 * Time — minutes / seconds / HH:mm:ss  ↔  canonical "HH:mm"
 * ------------------------------------------------------------------ */

/** Minutes since midnight (0–1439) → `"HH:mm"`; null / out of range → `""`. */
export function minutesToHhmm(minutes: number | null | undefined): string {
  if (minutes == null || minutes < 0 || minutes > 1439) return '';
  return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
}

/** `"HH:mm"` (minuty i jednociferné) → minutes since midnight (0–1439); empty / invalid → `null`. */
export function hhmmToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{1,2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Seconds since midnight (0–86399) → `"HH:mm"` (seconds dropped); null / out of range → `""`. */
export function secondsToHhmm(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0 || seconds > 86399) return '';
  return minutesToHhmm(Math.floor(seconds / 60));
}

/** `"HH:mm"` → seconds since midnight (whole minutes only); empty / invalid → `null`. */
export function hhmmToSeconds(value: string): number | null {
  const minutes = hhmmToMinutes(value);
  return minutes == null ? null : minutes * 60;
}

/** `"HH:mm:ss"` (or `"HH:mm"`) → canonical `"HH:mm"`; invalid → `""`. */
export function hhmmssToHhmm(value: string): string {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((value ?? '').trim());
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return '';
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/** Canonical `"HH:mm"` → `"HH:mm:ss"` (seconds always `00`); empty / invalid → `""`. */
export function hhmmToHhmmss(value: string): string {
  const canonical = hhmmssToHhmm(value);
  return canonical ? `${canonical}:00` : '';
}
